import React, { useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import {
  PhonicsLesson,
  PhonicsClassStatus,
  PhonicsClassLessonStatus,
  PhonicsLessonProgress,
} from '@/api/types';
import { PhonicsLessonPlayerKind } from '../LessonPlayer/types';
import { useData } from '@/context/Data';
import { Button, Confirmation } from '@/components';
import { TooltipPosition } from '@/context/Ui';

interface ManageLaunchLessonLinkProps {
  lesson: PhonicsLesson;
  phonicsClass: PhonicsClassStatus;
  kind?: PhonicsLessonPlayerKind;
  label?: string;
  naked?: boolean;
  onLaunchLesson?: (
    lesson: PhonicsLesson,
    kind: PhonicsLessonPlayerKind,
  ) => void;
  tooltipPosition?: TooltipPosition;
}

export const ManageLaunchLessonLink = ({
  children,
  lesson,
  phonicsClass,
  kind = 'reading',
  label = 'Launch Lesson',
  naked = false,
  onLaunchLesson,
  tooltipPosition = 'right',
}: React.PropsWithChildren<ManageLaunchLessonLinkProps>) => {
  const router = useRouter();
  const { currentClass } = useData();

  const [lessonStatus, setLessonStatus] = useState<
    PhonicsClassLessonStatus | undefined
  >(undefined);

  const [requiresConfirmation, setRequiresConfirmation] =
    useState<boolean>(false);

  const [confirmationMessage, setConfirmationMessage] = useState<string>('');

  const doLaunchLesson = useCallback(() => {
    onLaunchLesson
      ? onLaunchLesson(lesson, kind)
      : router.push(`/play/${lesson.id}?kind=${kind}`);
  }, [lesson, kind, onLaunchLesson, router]);

  const tryLaunchLesson = useCallback(() => {
    if (lessonStatus?.progress === PhonicsLessonProgress.COMPLETED) {
      setRequiresConfirmation(true);
      setConfirmationMessage(`
          ${currentClass?.label} has already completed lesson ${lesson.number}. Are
          you sure you want to do it again?
        `);

      return;
    }

    if (phonicsClass.currentLessonId !== lesson.id) {
      setRequiresConfirmation(true);
      setConfirmationMessage(`
          Lesson ${lesson.number} is not the next lesson in the sequence for
          ${currentClass?.label}. Are you sure you want to do it now?
        `);

      return;
    }

    doLaunchLesson();
  }, [currentClass, lesson, lessonStatus, phonicsClass, doLaunchLesson]);

  useEffect(() => {
    setLessonStatus(
      phonicsClass.classLessonStatuses.find((l) => l.lessonId === lesson.id),
    );
  }, [phonicsClass, lesson]);

  return (
    <>
      {naked ? (
        <button onClick={tryLaunchLesson}>{children ? children : label}</button>
      ) : (
        <Button
          onClick={tryLaunchLesson}
          tooltipText={
            kind === 'reading'
              ? 'Launch the lesson in reading mode'
              : 'Launch the lesson in spelling mode'
          }
          ariaLabel={
            kind === 'reading'
              ? 'Launch the lesson in reading mode'
              : 'Launch the lesson in spelling mode'
          }
          tooltipPosition={tooltipPosition}
          lessPadding
        >
          {children ? children : label}
        </Button>
      )}

      <Confirmation
        visible={requiresConfirmation}
        title="Launch Phonics lesson"
        message={confirmationMessage}
        onConfirm={() => {
          setRequiresConfirmation(false);
          setConfirmationMessage('');

          doLaunchLesson();
        }}
        onCancel={() => {
          setRequiresConfirmation(false);
          setConfirmationMessage('');
        }}
      />
    </>
  );
};
